<template>
  <div class="format-dropdown" ref="formatDropdownRef">
    <button
      @click="toggleFormatDropdown"
      class="format-dropdown-trigger"
      :class="{ 'is-active': isTextFormatActive }"
    >
      <span v-if="editor.isActive('heading', { level: 1 })">H1</span>
      <span v-else-if="editor.isActive('heading', { level: 2 })">H2</span>
      <span v-else-if="editor.isActive('heading', { level: 3 })">H3</span>
      <span v-else-if="editor.isActive('heading', { level: 4 })">H4</span>
      <span v-else>Text</span>
      <img src="/images/tiptap-bar/chevron-down.svg" alt="Dropdown" />
    </button>
    <div v-if="showFormatDropdown" class="format-dropdown-menu">
      <button
        @click="applyFormat('paragraph')"
        :class="{ 'is-active': !editor.isActive('heading') }"
      >
        Paragraph
      </button>
      <button
        @click="applyFormat('h1')"
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
      >
        Heading 1
      </button>
      <button
        @click="applyFormat('h2')"
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
      >
        Heading 2
      </button>
      <button
        @click="applyFormat('h3')"
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
      >
        Heading 3
      </button>
      <button
        @click="applyFormat('h4')"
        :class="{ 'is-active': editor.isActive('heading', { level: 4 }) }"
      >
        Heading 4
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
});

const formatDropdownRef = ref(null);
const showFormatDropdown = ref(false);
const isTextFormatActive = ref(false);

const toggleFormatDropdown = () => {
  showFormatDropdown.value = !showFormatDropdown.value;
  isTextFormatActive.value = false;
};

const applyFormat = (format) => {
  if (format === "paragraph") {
    props.editor.chain().focus().setParagraph().run();
  } else {
    props.editor
      .chain()
      .focus()
      .toggleHeading({ level: parseInt(format.replace("h", "")) })
      .run();
  }
  showFormatDropdown.value = false;
  isTextFormatActive.value = format === "paragraph";
};

// Handle clicking outside the format dropdown
onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);
});

const handleOutsideClick = (event) => {
  const dropdown = formatDropdownRef.value;
  if (dropdown && !dropdown.contains(event.target)) {
    showFormatDropdown.value = false;
  }
};
</script>

<style scoped>
/* Format dropdown styles */
.format-dropdown {
  position: relative;
  display: inline-block;
}

.format-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 0.9em;
  min-width: 75px;
  justify-content: space-between;
}

.format-dropdown-trigger:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.format-dropdown-trigger.is-active {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.format-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 5px;
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 150px;
  max-height: 250px;
  overflow-y: auto;
}

.format-dropdown-menu button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9em;
  border-radius: 0;
}

.format-dropdown-menu button:hover {
  background-color: #f8f9fa;
}

.format-dropdown-menu button.is-active {
  background-color: #e6f7ff;
  color: #0d6efd;
  font-weight: 500;
}

/* Style each heading option to look like the actual heading */
.format-dropdown-menu button:nth-child(2) {
  font-size: 1.4em;
  font-weight: bold;
}

.format-dropdown-menu button:nth-child(3) {
  font-size: 1.3em;
  font-weight: bold;
}

.format-dropdown-menu button:nth-child(4) {
  font-size: 1.15em;
  font-weight: bold;
}

.format-dropdown-menu button:nth-child(5) {
  font-size: 1em;
  font-weight: bold;
}
</style>
